import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Video, VideoOff, Mic, MicOff, Phone, Send, DollarSign, Users, Lock, Shield, Radio, Zap, Crown, Clock, Heart } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { creatorAPI, walletAPI } from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import TipMenu from '../components/creator/TipMenu';
import GoalMeter from '../components/creator/GoalMeter';
import TipRace from '../components/creator/TipRace';
import ExplodingMedia from '../components/creator/ExplodingMedia';
import Bridges from '../components/guidance/Bridges';

export default function CreatorRoomPage() {
  const { id: roomId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [room, setRoom] = useState(null);
  const [access, setAccess] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chatMsgs, setChatMsgs] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [tipAmount, setTipAmount] = useState('');
  const [showTip, setShowTip] = useState(false);
  const [sessionTime, setSessionTime] = useState(0);
  const [walletBal, setWalletBal] = useState(0);
  const [goalCurrent, setGoalCurrent] = useState(undefined);
  const [activeTab, setActiveTab] = useState('chat'); // chat | features

  // Video
  const [inCall, setInCall] = useState(false);
  const [videoOn, setVideoOn] = useState(false);
  const [audioOn, setAudioOn] = useState(true);
  const localRef = useRef(null);
  const remoteRef = useRef(null);
  const peerRef = useRef(null);
  const streamRef = useRef(null);
  const wsRef = useRef(null);
  const chatEndRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    const fetch = async () => {
      try {
        const [rRes, aRes, wRes] = await Promise.all([
          creatorAPI.getRoom(roomId),
          creatorAPI.checkAccess(roomId),
          walletAPI.getBalance().catch(() => ({ data: { balance: 0 } })),
        ]);
        setRoom(rRes.data);
        setAccess(aRes.data);
        setWalletBal(wRes.data.balance || 0);
      } catch (e) { toast.error('Room not found'); navigate('/creators'); }
      finally { setLoading(false); }
    };
    fetch();
  }, [roomId, navigate]);

  // WebSocket for chat
  useEffect(() => {
    if (!roomId || !access?.has_access) return;
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsHost = process.env.REACT_APP_BACKEND_URL.replace(/^https?:\/\//, '');
    const ws = new WebSocket(`${wsProtocol}//${wsHost}/api/ws/room/${roomId}`);
    wsRef.current = ws;

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      if (data.type === 'chat') {
        setChatMsgs(prev => [...prev, { sender: data.sender, text: data.text, ts: Date.now() }]);
      } else if (data.type === 'offer') handleOffer(data);
      else if (data.type === 'answer') handleAnswer(data);
      else if (data.type === 'ice-candidate') handleIce(data);
      else if (data.type === 'peer_joined') toast.info('Someone joined the room');
    };

    const interval = setInterval(() => { if (ws.readyState === 1) ws.send(JSON.stringify({ type: 'ping' })); }, 25000);
    return () => { clearInterval(interval); ws.close(); };
  }, [roomId, access]);

  // Session timer (45 min limit)
  useEffect(() => {
    if (inCall) {
      timerRef.current = setInterval(() => {
        setSessionTime(prev => {
          if (prev >= 2700) { // 45 min
            toast.warning('Free session limit reached (45 min). Reconnect to continue.');
            endCall();
            return 0;
          }
          return prev + 1;
        });
      }, 1000);
    }
    return () => clearInterval(timerRef.current);
  }, [inCall]);

  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [chatMsgs]);

  const startCall = async () => {
    try {
      const turnRes = await creatorAPI.getTurnConfig();
      const config = turnRes.data;

      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      streamRef.current = stream;
      if (localRef.current) localRef.current.srcObject = stream;
      setVideoOn(true);
      setInCall(true);

      const pc = new RTCPeerConnection(config);
      peerRef.current = pc;
      stream.getTracks().forEach(t => pc.addTrack(t, stream));
      pc.ontrack = (e) => { if (remoteRef.current) remoteRef.current.srcObject = e.streams[0]; };
      pc.onicecandidate = (e) => { if (e.candidate && wsRef.current?.readyState === 1) wsRef.current.send(JSON.stringify({ type: 'ice-candidate', candidate: e.candidate })); };

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      wsRef.current?.send(JSON.stringify({ type: 'offer', sdp: offer.sdp }));
    } catch (e) { toast.error('Camera/mic access required'); }
  };

  const handleOffer = async (data) => {
    try {
      const turnRes = await creatorAPI.getTurnConfig();
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      streamRef.current = stream;
      if (localRef.current) localRef.current.srcObject = stream;
      setVideoOn(true); setInCall(true);

      const pc = new RTCPeerConnection(turnRes.data);
      peerRef.current = pc;
      stream.getTracks().forEach(t => pc.addTrack(t, stream));
      pc.ontrack = (e) => { if (remoteRef.current) remoteRef.current.srcObject = e.streams[0]; };
      pc.onicecandidate = (e) => { if (e.candidate && wsRef.current?.readyState === 1) wsRef.current.send(JSON.stringify({ type: 'ice-candidate', candidate: e.candidate })); };

      await pc.setRemoteDescription(new RTCSessionDescription({ type: 'offer', sdp: data.sdp }));
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      wsRef.current?.send(JSON.stringify({ type: 'answer', sdp: answer.sdp }));
    } catch (e) { toast.error('Failed to join call'); }
  };

  const handleAnswer = async (data) => { if (peerRef.current) await peerRef.current.setRemoteDescription(new RTCSessionDescription({ type: 'answer', sdp: data.sdp })); };
  const handleIce = async (data) => { if (peerRef.current && data.candidate) try { await peerRef.current.addIceCandidate(new RTCIceCandidate(data.candidate)); } catch {} };

  const endCall = () => {
    streamRef.current?.getTracks().forEach(t => t.stop());
    peerRef.current?.close();
    peerRef.current = null; streamRef.current = null;
    setInCall(false); setVideoOn(false); setSessionTime(0);
  };

  const toggleVideo = () => { const t = streamRef.current?.getVideoTracks()[0]; if (t) { t.enabled = !t.enabled; setVideoOn(t.enabled); } };
  const toggleAudio = () => { const t = streamRef.current?.getAudioTracks()[0]; if (t) { t.enabled = !t.enabled; setAudioOn(t.enabled); } };

  const sendChat = () => {
    if (!chatInput.trim() || !wsRef.current) return;
    wsRef.current.send(JSON.stringify({ type: 'chat', sender: user?.name || 'User', text: chatInput }));
    setChatMsgs(prev => [...prev, { sender: user?.name || 'You', text: chatInput, ts: Date.now(), mine: true }]);
    setChatInput('');
  };

  const handleSubscribe = async () => {
    try {
      const res = await creatorAPI.subscribe(roomId);
      toast.success(res.data.message);
      setAccess({ has_access: true, role: 'subscriber' });
      const wRes = await walletAPI.getBalance();
      setWalletBal(wRes.data.balance || 0);
    } catch (e) { toast.error(e.response?.data?.detail || 'Subscription failed'); }
  };

  const handleTip = async () => {
    const amt = parseFloat(tipAmount);
    if (isNaN(amt) || amt <= 0) return;
    try {
      const res = await creatorAPI.tip(roomId, amt);
      toast.success(res.data.message);
      setTipAmount(''); setShowTip(false);
      const wRes = await walletAPI.getBalance();
      setWalletBal(wRes.data.balance || 0);
    } catch (e) { toast.error(e.response?.data?.detail || 'Tip failed'); }
  };

  const handleGoLive = async () => {
    try {
      const res = await creatorAPI.goLive(roomId);
      setRoom(prev => ({ ...prev, is_live: res.data.is_live }));
      toast.success(res.data.is_live ? 'You are LIVE!' : 'Stream ended');
    } catch (e) { toast.error('Failed'); }
  };

  const refreshWallet = async () => {
    try { const w = await walletAPI.getBalance(); setWalletBal(w.data.balance || 0); } catch {}
  };

  const handleTipFromFeature = (amount) => {
    setGoalCurrent(prev => (prev || 0) + amount);
    refreshWallet();
  };

  const formatTime = (s) => `${Math.floor(s/60)}:${String(s%60).padStart(2,'0')}`;

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-zinc-950"><div className="w-8 h-8 border-2 border-rose-500 border-t-transparent rounded-full animate-spin" /></div>;
  if (!room) return null;

  const isCreator = access?.role === 'creator';
  const hasAccess = access?.has_access;

  // Paywall
  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
          className="max-w-md w-full text-center" data-testid="paywall">
          <div className={`w-full h-48 rounded-t-2xl ${room.is_adult ? 'bg-gradient-to-br from-rose-900 to-pink-900' : 'bg-gradient-to-br from-violet-600 to-cyan-500'} flex items-center justify-center`}>
            <Lock className="text-white/30" size={64} />
          </div>
          <div className="bg-zinc-900 rounded-b-2xl p-6 border border-zinc-800 border-t-0">
            <h2 className="font-display font-bold text-xl text-white mb-1">{room.name}</h2>
            <p className="text-zinc-400 text-sm mb-1">by {room.creator_name}</p>
            {room.preview_text && <p className="text-zinc-500 text-xs mb-4 italic">"{room.preview_text}"</p>}
            <div className="flex items-center justify-center gap-4 mb-4 text-zinc-400 text-xs">
              <span className="flex items-center gap-1"><Users size={12} /> {room.subscriber_count} subs</span>
              <span>{room.category}</span>
              <span>{room.is_broadcast ? 'Broadcast' : '1-on-1'}</span>
            </div>
            <div className="flex items-center gap-1 justify-center mb-4 text-green-400 text-xs">
              <Shield size={12} /> E2E Encrypted
            </div>
            <p className="text-3xl font-display font-bold text-white mb-1">{formatCurrency(room.price_per_month)}<span className="text-sm text-zinc-400 font-normal">/month</span></p>
            <p className="text-xs text-zinc-500 mb-4">Cancel anytime</p>
            <p className="text-xs text-zinc-500 mb-4">Wallet: {formatCurrency(walletBal)}</p>
            <Button onClick={handleSubscribe} disabled={walletBal < room.price_per_month}
              className="w-full bg-gradient-to-r from-rose-500 to-pink-600 text-white font-bold rounded-xl py-5" data-testid="subscribe-btn">
              {walletBal < room.price_per_month ? `Need ${formatCurrency(room.price_per_month - walletBal)} more` : `Subscribe - ${formatCurrency(room.price_per_month)}/mo`}
            </Button>
            {walletBal < room.price_per_month && (
              <Link to="/wallet" className="text-xs text-rose-400 underline mt-2 block">Add funds to wallet</Link>
            )}
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 flex flex-col" data-testid="creator-room-view">
      {/* Header */}
      <div className="bg-zinc-900 border-b border-zinc-800 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/creators"><Button variant="ghost" size="sm" className="text-zinc-400 hover:text-white">&larr;</Button></Link>
          <div>
            <h1 className="font-display font-bold text-white text-sm">{room.name}</h1>
            <p className="text-xs text-zinc-400">{room.creator_name} &middot; {room.subscriber_count} subs &middot; {formatCurrency(room.price_per_month)}/mo</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {inCall && (
            <div className="flex items-center gap-1 px-2 py-1 bg-zinc-800 rounded-full">
              <Clock size={12} className={sessionTime > 2400 ? 'text-red-400' : 'text-zinc-400'} />
              <span className={`text-xs font-mono ${sessionTime > 2400 ? 'text-red-400' : 'text-zinc-400'}`}>{formatTime(sessionTime)} / 45:00</span>
            </div>
          )}
          {room.is_live && <span className="flex items-center gap-1 px-2 py-1 bg-red-500/20 rounded-full"><span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" /><span className="text-xs text-red-400 font-bold">LIVE</span></span>}
          <div className="flex items-center gap-1 px-2 py-1 bg-green-900/30 rounded-full border border-green-700/30">
            <Shield size={10} className="text-green-400" />
            <span className="text-[10px] text-green-400">Encrypted</span>
          </div>
          {isCreator && (
            <Button onClick={handleGoLive} size="sm"
              className={`rounded-full text-xs font-bold ${room.is_live ? 'bg-red-500 text-white' : 'bg-[#b8ff00] text-black'}`} data-testid="go-live-btn">
              {room.is_live ? 'End Stream' : 'Go Live'}
            </Button>
          )}
        </div>
      </div>

      <div className="flex-1 flex flex-col lg:flex-row min-h-0">
        {/* Video */}
        <div className="lg:w-2/3 bg-zinc-900 p-3 flex flex-col">
          <div className="relative flex-1 rounded-xl overflow-hidden bg-zinc-800 min-h-[300px]">
            {/* Main video area: local stream fills it when broadcasting solo */}
            <video ref={localRef} autoPlay playsInline muted className="w-full h-full object-cover" style={{ display: inCall ? 'block' : 'none' }} />
            {/* Remote overlay (when someone joins, remote takes a PiP spot) */}
            <video ref={remoteRef} autoPlay playsInline className={inCall ? 'absolute bottom-3 right-3 w-48 h-36 rounded-lg border-2 border-zinc-600 object-cover bg-zinc-900' : 'hidden'} />
            {!inCall && (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-zinc-800">
                <div className={`w-24 h-24 rounded-full flex items-center justify-center mb-4 ${room.is_adult ? 'bg-rose-900/50' : 'bg-violet-900/50'}`}>
                  <Radio size={40} className={room.is_adult ? 'text-rose-400' : 'text-violet-400'} />
                </div>
                <p className="text-zinc-400 text-sm mb-4">{isCreator ? 'Start your stream' : 'Join the room'}</p>
                <Button onClick={startCall} className="bg-[#b8ff00] text-black font-bold rounded-full px-8" data-testid="join-call-btn">
                  <Video size={16} className="mr-2" /> {isCreator ? 'Start Broadcasting' : 'Join Stream'}
                </Button>
                <p className="text-xs text-zinc-500 mt-2">Free tier: 45-min sessions</p>
              </div>
            )}
          </div>
          {inCall && (
            <div className="flex items-center justify-center gap-3 mt-3">
              <Button onClick={toggleVideo} variant="ghost" className={`w-10 h-10 rounded-full ${videoOn ? 'bg-zinc-700 text-white' : 'bg-red-500/20 text-red-400'}`}>{videoOn ? <Video size={18} /> : <VideoOff size={18} />}</Button>
              <Button onClick={toggleAudio} variant="ghost" className={`w-10 h-10 rounded-full ${audioOn ? 'bg-zinc-700 text-white' : 'bg-red-500/20 text-red-400'}`}>{audioOn ? <Mic size={18} /> : <MicOff size={18} />}</Button>
              <Button onClick={endCall} className="w-12 h-12 rounded-full bg-red-500 text-white"><Phone size={18} className="rotate-[135deg]" /></Button>
            </div>
          )}
        </div>

        {/* Chat + Features */}
        <div className="flex-1 flex flex-col min-h-0 bg-zinc-950 border-l border-zinc-800">
          {/* Tab switcher */}
          <div className="flex border-b border-zinc-800 bg-zinc-900">
            <button onClick={() => setActiveTab('chat')} className={`flex-1 py-2 text-xs font-bold transition-colors ${activeTab === 'chat' ? 'text-[#b8ff00] border-b-2 border-[#b8ff00]' : 'text-zinc-500'}`} data-testid="tab-chat">Chat</button>
            <button onClick={() => setActiveTab('features')} className={`flex-1 py-2 text-xs font-bold transition-colors ${activeTab === 'features' ? 'text-rose-400 border-b-2 border-rose-400' : 'text-zinc-500'}`} data-testid="tab-features">
              Tips & More
            </button>
          </div>

          {activeTab === 'chat' ? (
            <>
              {/* Goal Meter always visible */}
              <GoalMeter roomId={roomId} isCreator={isCreator} externalCurrent={goalCurrent} />

              <div className="flex-1 overflow-y-auto p-4 space-y-2 min-h-0">
                {chatMsgs.map((m, i) => (
                  <div key={i} className={`flex ${m.mine ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm ${m.mine ? 'bg-[#b8ff00] text-black rounded-br-sm' : 'bg-zinc-800 text-zinc-200 rounded-bl-sm'}`}>
                      {!m.mine && <p className="text-xs text-zinc-400 mb-0.5">{m.sender}</p>}
                      <p>{m.text}</p>
                    </div>
                  </div>
                ))}
                <div ref={chatEndRef} />
              </div>

              {/* Tip Panel */}
              <AnimatePresence>
                {showTip && (
                  <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} className="border-t border-zinc-800 overflow-hidden">
                    <div className="p-3 bg-zinc-900 flex items-center gap-2">
                      <Heart size={16} className="text-rose-400" />
                      <div className="flex gap-1">
                        {[1, 5, 10, 25].map(a => (
                          <Button key={a} size="sm" variant="outline" onClick={() => setTipAmount(a.toString())}
                            className={`rounded-full text-xs border-zinc-700 ${tipAmount === a.toString() ? 'bg-rose-500/20 border-rose-400 text-rose-400' : 'text-zinc-400'}`}>${a}</Button>
                        ))}
                      </div>
                      <Input type="number" value={tipAmount} onChange={e => setTipAmount(e.target.value)} placeholder="$" className="w-16 bg-zinc-800 border-zinc-700 text-white h-8 text-sm" data-testid="tip-input" />
                      <Button onClick={handleTip} size="sm" disabled={!tipAmount} className="bg-rose-500 text-white rounded-full text-xs font-bold" data-testid="send-tip-btn">Send</Button>
                      <Button onClick={() => setShowTip(false)} variant="ghost" size="sm" className="text-zinc-400">X</Button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Chat Input */}
              <div className="border-t border-zinc-800 bg-zinc-900 p-3 flex items-center gap-2">
                <Input placeholder="Chat..." value={chatInput} onChange={e => setChatInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && sendChat()}
                  className="flex-1 bg-zinc-800 border-zinc-700 text-white h-10 rounded-full px-4" data-testid="creator-chat-input" />
                <Button onClick={sendChat} size="sm" className="bg-[#b8ff00] text-black rounded-full w-10 h-10"><Send size={16} /></Button>
                {!isCreator && (
                  <Button onClick={() => setShowTip(!showTip)} variant="ghost" size="sm" className="text-rose-400 rounded-full w-10 h-10" data-testid="tip-btn"><Heart size={16} /></Button>
                )}
              </div>
            </>
          ) : (
            /* Features Tab */
            <div className="flex-1 overflow-y-auto min-h-0">
              <TipMenu roomId={roomId} isCreator={isCreator} onTipSent={handleTipFromFeature} />
              <GoalMeter roomId={roomId} isCreator={isCreator} externalCurrent={goalCurrent} />
              <TipRace roomId={roomId} isCreator={isCreator} onTipSent={handleTipFromFeature} />
              <ExplodingMedia roomId={roomId} isCreator={isCreator} />
              {/* Wallet info + Bridges */}
              <div className="border-t border-zinc-800 px-3 py-2">
                <div className="flex items-center justify-between text-xs text-zinc-500">
                  <span>Wallet: {formatCurrency(walletBal)}</span>
                  <Link to="/wallet" className="text-[#b8ff00] hover:underline">Add funds</Link>
                </div>
                <Bridges pageType="room" entityId={roomId} />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
